import { storeToRefs } from 'pinia'
import { computed, type ComputedRef } from 'vue'

import { isBoundDeviceState } from '@/components/newProductPanel/newProductPanel.type'
import { UnboundDeviceCategoryType } from '@/graphql/creator-platform/generated'
import { getBoundDeviceProductSets, getUnboundDeviceProductSets } from '@/store/newProduct'
import { useNewProductStore } from '@/store/newProductStore'
import {
  isBoundDeviceProductIndicator,
  type NewProductIndicator,
} from '@/store/newProductStore.type'
import { getDeviceImageUrl } from '@/utils/getDeviceImage'
import { type Rect } from '@/utils/type.util'

export interface NewProductDisplay {
  readonly compareAtPrice: number
  readonly designRect: Rect
  readonly postDesignLayers: readonly string[]
  readonly preDesignLayers: readonly string[]
  readonly previewMaskUrl: string
  readonly price: number
  readonly printMaskUrl: string
  readonly viewableRect: Rect
}

export interface UseProductDisplay {
  readonly currentProductDisplay: ComputedRef<NewProductDisplay | null>
}

export async function getProductDisplay(
  indicator: NewProductIndicator,
): Promise<NewProductDisplay> {
  const productSets = isBoundDeviceProductIndicator(indicator)
    ? await getBoundDeviceProductSets(indicator.deviceHandle)
    : await getUnboundDeviceProductSets(indicator.categoryType)
  const productSet = productSets.find((p) =>
    p.products.some((p) => p.handle === indicator.productHandle),
  )
  const product = productSet?.products.find((p) => p.handle === indicator.productHandle)
  const variant = product?.variants.find((v) => v.color.handle === indicator.colorHandle)

  if (productSet === undefined || product === undefined || variant === undefined) {
    throw new Error(
      'getProductDisplay: productSet === undefined || product === undefined || variant === undefined',
    )
  }

  const { preDesignLayers, postDesignLayers, compareAtPrice, price, mpn } = variant
  const { previewMaskUrl, viewableRect, printMaskUrl } = product

  return {
    preDesignLayers:
      isBoundDeviceProductIndicator(indicator) && indicator.deviceColorHandle !== null
        ? [
            getDeviceImageUrl(indicator.deviceHandle, mpn, indicator.deviceColorHandle),
            ...preDesignLayers,
          ]
        : preDesignLayers,
    postDesignLayers,
    previewMaskUrl,
    printMaskUrl,
    designRect: productSet.boundDesignGroup.designRect,
    compareAtPrice,
    price,
    viewableRect,
  }
}

type AquastandPreviewType = 'frontal' | 'perspective'
export interface ProductDisplayProps {
  readonly aquastandPreviewType?: AquastandPreviewType
}

export function useCurrentProductDisplay({
  aquastandPreviewType = 'frontal',
}: ProductDisplayProps = {}): ComputedRef<NewProductDisplay | null> {
  const productStore = useNewProductStore()

  const { productState } = storeToRefs(productStore)

  const currentProductDisplay = computed((): NewProductDisplay | null => {
    const state = productState.value
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (state === null || state.selectedProduct === null) return null

    const { product, variant, productSet } = state.selectedProduct
    const { previewMaskUrl, viewableRect, printMaskUrl } = product
    const {
      compareAtPrice,
      price,
      preDesignLayers: rawPreDesignLayers,
      postDesignLayers: rawPostDesignLayers,
      mpn,
    } = variant

    let mask = previewMaskUrl
    let preDesignLayers = rawPreDesignLayers
    let postDesignLayers = rawPostDesignLayers
    if (isBoundDeviceState(state)) {
      const { color, device } = state
      if (color !== null) {
        preDesignLayers = [getDeviceImageUrl(device.handle, mpn, color.handle), ...preDesignLayers]
      }
    }

    if (
      state.categoryType === UnboundDeviceCategoryType.AquaStand &&
      aquastandPreviewType === 'perspective'
    ) {
      // eslint-disable-next-line unicorn/consistent-destructuring
      const { imageUrl, previewMaskUrl } = state.selectedProduct.variant.aquaStandPerspective
      preDesignLayers = [imageUrl]
      postDesignLayers = []
      mask = previewMaskUrl
    }

    return {
      preDesignLayers,
      postDesignLayers,
      previewMaskUrl: mask,
      printMaskUrl,
      designRect: productSet.boundDesignGroup.designRect,
      compareAtPrice,
      price,
      viewableRect,
    }
  })

  return currentProductDisplay
}
